"use client";

import * as React from "react";
import { ArrowDown, ArrowUp, ArrowUpDown, Inbox, MoreHorizontal } from "lucide-react";
import useTranslation from "next-translate/useTranslation";
import { WIDTH_MD } from "@constants/global";
import { useWindowWidth } from "@react-hook/window-size";
import { cn } from "@/lib/utils";
import { Card } from "components/ui/v2/card";
import {
  LiquidPillDropdown,
  type LiquidPillDropdownItem,
} from "components/ui/v2/liquid-pill-dropdown";
import type { IColumn } from "components/ui/v2/maintainer/types";
import { PagePaginationFooterV2 } from "./page-pagination-footer-v2";

export interface MaintainerDataTableV2PaginationProps {
  currentPage: number;
  totalItems: number;
  pageSize: number;
  onPageChange: (page: number) => void | Promise<void>;
  totalPages?: number;
  rangeLabel?: React.ReactNode;
  perPageLabel?: string;
  siblingCount?: number;
}

export interface MaintainerDataTableV2Props<TData extends object> {
  data: TData[];
  columns: IColumn<TData>[];
  loading?: boolean;
  rowKey?: keyof TData | ((row: TData) => string);
  onRowClick?: (row: TData) => void;
  /** Standard row actions menu (replaces TableActions column on mobile) */
  getRowActions?: (row: TData) => LiquidPillDropdownItem[];
  /** Handler for `getRowActions` item selection (required for mobile menu) */
  onRowActionSelect?: (value: string, row: TData) => void;
  mobileCardRender?: (row: TData) => React.ReactNode;
  className?: string;
  skeletonRows?: number;
  /** Contenido a mostrar cuando no hay filas (default: estado vacío genérico). */
  emptyState?: React.ReactNode;
  /** Orden activo (controlado). Solo columnas con `sortable: true` lo usan. */
  sort?: { dataKey: string; order: "asc" | "desc" } | null;
  /** Click en header: cicla asc → desc → null. Sin esta prop los headers no son clickeables. */
  onSortChange?: (dataKey: string, order: "asc" | "desc" | null) => void;
  /** Paginación v2 renderizada dentro de la tabla, bajo las filas. */
  pagination?: MaintainerDataTableV2PaginationProps;
  /**
   * Densidad compacta: menos padding y tipografía más pequeña en header/celdas.
   */
  compact?: boolean;
  /**
   * Fija el `<thead>` al hacer scroll vertical dentro del contenedor de la tabla.
   * Requiere `maxHeight` (o un contenedor con altura acotada).
   */
  stickyHeader?: boolean;
  /**
   * Altura máxima del área scrollable (útil con `stickyHeader` / columnas `fixed`).
   * Ej: `"min(70vh, 720px)"` o `480`.
   */
  maxHeight?: number | string;
  /**
   * Marca filas de body como sticky (top). Se apilan bajo el header si `stickyHeader`.
   * Usar con moderación (p. ej. 1 fila de acciones/filtros inline).
   */
  isRowSticky?: (row: TData, index: number) => boolean;
  /** Legacy Table props — accepted for migration compat; mapped to the internal v2 pagination footer. */
  forceDisplayableLength?: number;
  handleChangeLimit?: (length: number) => void | Promise<void>;
  handleChangePage?: (page: number) => void | Promise<void>;
  totalCount?: number;
  currentPage?: number;
  layout?: string[];
  prev?: boolean;
  next?: boolean;
  first?: boolean;
  last?: boolean;
  totalLabel?: string;
  containerClassname?: string;
  containerStyle?: React.CSSProperties;
  resetOnNewData?: boolean;
  minHeight?: number;
  hover?: boolean;
  ellipsis?: boolean;
  [key: string]: unknown;
}

function resolveFixedSide(
  fixed?: boolean | "left" | "right",
): "left" | "right" | null {
  if (fixed === true || fixed === "left") return "left";
  if (fixed === "right") return "right";
  return null;
}

function stickyColumnClass(
  side: "left" | "right" | null,
  opts: { isHeader?: boolean } = {},
): string | undefined {
  if (!side) return undefined;
  // Right (actions) must sit above scrolling body cells and left sticky cols,
  // so LiquidPill / overflowing neighbors never paint over the ⋯ button.
  const z = opts.isHeader
    ? side === "right"
      ? "z-[6]"
      : "z-[4]"
    : side === "right"
      ? "z-[5]"
      : "z-[2]";
  // Solid whites only — no drop-shadow (it reads as a dark strip on the sticky edge).
  const bg = opts.isHeader
    ? "bg-slate-50 dark:bg-zinc-900"
    : "bg-white dark:bg-zinc-900 group-hover:bg-white dark:group-hover:bg-zinc-900";
  const edge =
    side === "left"
      ? "border-r border-slate-100 dark:border-zinc-800"
      : "border-l border-slate-100 dark:border-zinc-800";
  return cn(
    "sticky isolate",
    side === "left" ? "left-0" : "right-0",
    z,
    bg,
    edge,
  );
}

function columnSizeStyle(
  col: {
    width?: number;
    fixed?: boolean | "left" | "right";
    cellStyle?: React.CSSProperties;
    headerStyle?: React.CSSProperties;
  },
  kind: "header" | "cell",
): React.CSSProperties {
  const baseStyle = kind === "header" ? col.headerStyle : col.cellStyle;
  if (col.width == null) return { ...baseStyle };
  const isFixed = Boolean(resolveFixedSide(col.fixed));
  return {
    width: col.width,
    minWidth: col.width,
    // Lock sticky columns so neighbors never bleed underneath.
    ...(isFixed ? { maxWidth: col.width } : {}),
    boxSizing: "border-box",
    ...baseStyle,
  };
}

function resolveRowKey<TData extends object>(
  row: TData,
  rowKey: MaintainerDataTableV2Props<TData>["rowKey"],
  index: number,
): string {
  if (typeof rowKey === "function") return rowKey(row);
  if (rowKey && (row as Record<string, unknown>)[rowKey as string] != null) {
    return String((row as Record<string, unknown>)[rowKey as string]);
  }
  if ("id" in row && (row as { id?: unknown }).id != null) {
    return String((row as { id: unknown }).id);
  }
  return String(index);
}

function TableSkeleton({ rows, cols }: { rows: number; cols: number }) {
  return (
    <div className="space-y-2" aria-hidden>
      {Array.from({ length: rows }).map((_, ri) => (
        <div
          key={`sk-row-${ri}`}
          className="flex gap-3 rounded-xl border border-slate-100 bg-white p-3 dark:border-zinc-800 dark:bg-zinc-900"
        >
          {Array.from({ length: Math.min(cols, 4) }).map((__, ci) => (
            <div
              key={`sk-cell-${ri}-${ci}`}
              className={cn(
                "h-4 animate-pulse rounded-md bg-slate-100 dark:bg-zinc-800",
                ci === 0 ? "w-8 shrink-0" : "flex-1",
              )}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

export function MaintainerDataTableV2<TData extends object>({
  data,
  columns,
  loading = false,
  rowKey,
  onRowClick,
  getRowActions,
  onRowActionSelect,
  mobileCardRender,
  className,
  skeletonRows = 6,
  emptyState,
  sort,
  onSortChange,
  pagination,
  compact = false,
  stickyHeader = false,
  maxHeight,
  isRowSticky,
  forceDisplayableLength,
  handleChangePage,
  totalCount,
  currentPage,
}: MaintainerDataTableV2Props<TData>) {
  const { t } = useTranslation("common");
  const wWidth = useWindowWidth();
  const isMobile = wWidth <= WIDTH_MD;

  const legacyPagination: MaintainerDataTableV2PaginationProps | undefined =
    !pagination &&
    currentPage !== undefined &&
    totalCount !== undefined &&
    handleChangePage
      ? {
          currentPage,
          totalItems: totalCount,
          pageSize: forceDisplayableLength ?? 10,
          onPageChange: handleChangePage,
        }
      : undefined;

  const effectivePagination = pagination ?? legacyPagination;
  const effectivePageSize = effectivePagination
    ? Math.max(1, effectivePagination.pageSize)
    : 1;
  const computedTotalPages = effectivePagination
    ? effectivePagination.totalPages ??
      Math.ceil(effectivePagination.totalItems / effectivePageSize)
    : 0;
  const shouldShowPagination = Boolean(effectivePagination);

  const paginationFooter = shouldShowPagination && effectivePagination ? (
    <div className="px-3 pb-4 sm:px-4">
      <PagePaginationFooterV2
        currentPage={effectivePagination.currentPage}
        totalPages={computedTotalPages}
        totalItems={effectivePagination.totalItems}
        pageSize={effectivePageSize}
        onPageChange={effectivePagination.onPageChange}
        siblingCount={effectivePagination.siblingCount}
        rangeLabel={
          effectivePagination.rangeLabel ?? (
            <>
              {t("Mostrando")} {effectivePagination.totalItems === 0
                ? 0
                : (effectivePagination.currentPage - 1) *
                    effectivePageSize + 1}{" "}
              -{" "}
              {Math.min(
                effectivePagination.currentPage * effectivePageSize,
                effectivePagination.totalItems,
              )}{" "}
              {t("de")} {effectivePagination.totalItems}
            </>
          )
        }
        perPageLabel={effectivePagination.perPageLabel}
      />
    </div>
  ) : null;

  if (loading) {
    return (
      <Card size="full" shadow="subtle" padding="none" className={className}>
        <div className="p-3 sm:p-4">
          <TableSkeleton rows={skeletonRows} cols={columns.length} />
        </div>
      </Card>
    );
  }

  if (data.length === 0) {
    return (
      <Card size="full" shadow="subtle" padding="none" className={className}>
        {emptyState ?? (
          <div className="flex min-h-[200px] flex-col items-center justify-center gap-3 p-8 text-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 dark:bg-zinc-800">
              <Inbox className="h-6 w-6 text-slate-400 dark:text-zinc-500" />
            </div>
            <p className="text-sm font-medium text-slate-600 dark:text-zinc-300">
              {t("No hay datos para mostrar")}
            </p>
          </div>
        )}
      </Card>
    );
  }

  if (isMobile) {
    return (
      <div className={cn("space-y-3", className)}>
        {data.map((row, index) => {
          const key = resolveRowKey(row, rowKey, index);
          const actions = getRowActions?.(row);

          if (mobileCardRender) {
            return <div key={key}>{mobileCardRender(row)}</div>;
          }

          return (
            <div
              key={key}
              className={cn(onRowClick && "cursor-pointer")}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
            >
            <Card
              size="full"
              shadow="subtle"
              padding="comfortable"
            >
              <div className="space-y-2">
                {columns
                  .filter((c) => c.header && c.header !== "Acciones")
                  .slice(0, 4)
                  .map((col) => (
                    <div key={String(col.dataKey)} className="min-w-0">
                      {col.header ? (
                        <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500 dark:text-zinc-400">
                          {col.header}
                        </p>
                      ) : null}
                      <div className="text-sm text-slate-900 dark:text-zinc-100">
                        {col.customCell
                          ? col.customCell({ rowData: row })
                          : String((row as Record<string, unknown>)[col.dataKey as string] ?? "—")}
                      </div>
                    </div>
                  ))}
              </div>
              {actions && actions.length > 0 ? (
                <div className="mt-3 flex justify-end border-t border-slate-100 pt-3 dark:border-zinc-800">
                  <LiquidPillDropdown
                    items={actions}
                    onSelect={(value) => onRowActionSelect?.(value, row)}
                    icon={<MoreHorizontal className="h-4 w-4" />}
                    variant="slate"
                    size="sm"
                    collapsedShape="circle"
                    reactive
                    aria-label={t("Opciones")}
                  />
                </div>
              ) : null}
            </Card>
            </div>
          );
        })}
        {paginationFooter}
      </div>
    );
  }

  const headerPad = compact ? "px-2 py-1.5" : "px-3 py-2.5";
  const cellPad = compact ? "px-2 py-1 whitespace-nowrap" : "px-3 py-2.5";
  const headerText = compact
    ? "text-[10px] font-semibold uppercase tracking-wide text-slate-500 dark:text-zinc-400"
    : "text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-zinc-400";
  const cellText = compact
    ? "text-xs text-slate-800 dark:text-zinc-200"
    : "text-sm text-slate-800 dark:text-zinc-200";
  const sortIconClass = compact ? "h-3 w-3" : "h-3.5 w-3.5";

  const resolvedMaxHeight =
    maxHeight == null
      ? undefined
      : typeof maxHeight === "number"
        ? `${maxHeight}px`
        : maxHeight;

  // Sticky header/columns need a scrollport. Card clips with overflow-hidden,
  // so this inner wrapper is the sticky containing block.
  const scrollStyle: React.CSSProperties | undefined = resolvedMaxHeight
    ? { maxHeight: resolvedMaxHeight }
    : undefined;

  // Count sticky body rows that appear before a given index (for top offset).
  const stickyBodyTopPx = stickyHeader ? (compact ? 32 : 40) : 0;
  const hasFixedColumns = columns.some((c) => Boolean(resolveFixedSide(c.fixed)));

  return (
    <Card size="full" shadow="subtle" padding="none" className={className}>
      {/* Scroll DENTRO del contenido de la Card: el wrapper de la
          Card aplica `overflow-hidden` (esquinas redondeadas), así que un
          overflow en la Card exterior no funciona — la tabla se clipa.
          Este div interno (w-full) es el verdadero contenedor scrollable. */}
      <div
        className={cn(
          "w-full",
          stickyHeader || resolvedMaxHeight || hasFixedColumns
            ? "overflow-auto"
            : "overflow-x-auto",
        )}
        style={scrollStyle}
      >
        <table
          className={cn(
            "w-full border-collapse text-left",
            compact ? "text-xs" : "text-sm",
          )}
          style={{ tableLayout: "auto" }}
        >
          <thead>
            <tr className="border-b border-slate-100 bg-slate-50 dark:border-zinc-800 dark:bg-zinc-900">
              {columns.map((col) => {
                const key = String(col.dataKey);
                const isSortable = Boolean(col.sortable && onSortChange);
                const activeOrder = sort?.dataKey === key ? sort.order : null;
                const fixedSide = resolveFixedSide(col.fixed);

                return (
                  <th
                    key={key}
                    className={cn(
                      headerPad,
                      headerText,
                      stickyHeader && "sticky top-0 z-[2] bg-slate-50 dark:bg-zinc-900",
                      stickyColumnClass(fixedSide, { isHeader: true }),
                    )}
                    style={columnSizeStyle(col, "header")}
                    aria-sort={
                      activeOrder === "asc"
                        ? "ascending"
                        : activeOrder === "desc"
                          ? "descending"
                          : undefined
                    }
                  >
                    {isSortable ? (
                      <button
                        type="button"
                        className="group/sort inline-flex items-center gap-1 rounded-sm transition-colors hover:text-slate-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/60 dark:hover:text-zinc-200"
                        onClick={() => {
                          const next =
                            activeOrder === null
                              ? "asc"
                              : activeOrder === "asc"
                                ? "desc"
                                : null;
                          onSortChange?.(key, next);
                        }}
                      >
                        {col.header}
                        {activeOrder === "asc" ? (
                          <ArrowUp className={sortIconClass} />
                        ) : activeOrder === "desc" ? (
                          <ArrowDown className={sortIconClass} />
                        ) : (
                          <ArrowUpDown
                            className={cn(
                              sortIconClass,
                              "opacity-0 transition-opacity group-hover/sort:opacity-60",
                            )}
                          />
                        )}
                      </button>
                    ) : (
                      col.header
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {data.map((row, index) => {
              const key = resolveRowKey(row, rowKey, index);
              const rowSticky = Boolean(isRowSticky?.(row, index));
              // Stack sticky body rows after the header (and after previous sticky rows).
              let stickyTop = stickyBodyTopPx;
              if (rowSticky && isRowSticky) {
                for (let i = 0; i < index; i += 1) {
                  if (isRowSticky(data[i], i)) {
                    stickyTop += compact ? 36 : 44;
                  }
                }
              }
              return (
                <tr
                  key={key}
                  className={cn(
                    "group border-b border-slate-50 transition-colors last:border-0 dark:border-zinc-800/80",
                    onRowClick &&
                      "cursor-pointer hover:bg-slate-50/80 dark:hover:bg-zinc-800/40",
                    rowSticky &&
                      "sticky z-[1] bg-white shadow-[0_1px_0_0_rgba(15,23,42,0.06)] dark:bg-zinc-900",
                  )}
                  style={rowSticky ? { top: stickyTop } : undefined}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                >
                  {columns.map((col) => {
                    const fixedSide = resolveFixedSide(col.fixed);
                    const cellContent = col.customCell
                      ? col.customCell({ rowData: row })
                      : String(
                          (row as Record<string, unknown>)[
                            col.dataKey as string
                          ] ?? "",
                        );
                    return (
                      <td
                        key={String(col.dataKey)}
                        className={cn(
                          cellPad,
                          "align-middle",
                          cellText,
                          stickyColumnClass(fixedSide),
                          rowSticky &&
                            !fixedSide &&
                            "bg-white dark:bg-zinc-900",
                          // Clip neighbors so pills never bleed under sticky actions.
                          (fixedSide || hasFixedColumns) && "overflow-hidden",
                        )}
                        style={columnSizeStyle(col, "cell")}
                      >
                        {fixedSide === "right" ? (
                          <div className="relative z-[1] flex items-center justify-center bg-white dark:bg-zinc-900">
                            {cellContent}
                          </div>
                        ) : (
                          cellContent
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {paginationFooter}
    </Card>
  );
}
